import { Link } from 'react-router-dom'

import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import { RoutePaths } from '@/routes/routes'
import { useQueryGetUsersActivity } from '@/services'
import {
  compareActivityTime,
  FIRST_SEEN_HINT,
  formatActivityTime,
  getUserActivityKey,
  LAST_ACTIVITY_HINT,
  LAST_LOGIN_HINT,
  USER_ACTIVITY_HELP_TEXT
} from '@/utils/user-activity'

type UserActivityTabProps = {
  userId?: string
  orgId?: string
  name?: string
  email?: string
}

const Pulse = ({ className = 'h-4 w-24' }: { className?: string }) => (
  <div className={`${className} rounded-md bg-muted/60 animate-pulse`} />
)

/**
 * Relative time with the absolute timestamp always visible (no tooltip),
 * so the tab reads the same in a screenshot.
 */
const ActivityTime = ({
  value,
  size = 'sm'
}: {
  value?: string | null
  size?: 'sm' | 'lg'
}) => {
  const { relative, absolute } = formatActivityTime(value)

  return (
    <div className="leading-tight">
      <div
        className={
          size === 'lg' ? 'text-xl font-semibold' : 'text-sm font-medium'
        }
      >
        {relative}
      </div>
      {absolute && (
        <div className="mt-0.5 text-xs text-muted-foreground/80 tabular-nums">
          {absolute}
        </div>
      )}
    </div>
  )
}

const getInitials = (name?: string, email?: string) =>
  (name || email || '?')
    .split(/[\s@._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('')

export const UserActivityTab = ({
  userId,
  orgId,
  name,
  email
}: UserActivityTabProps) => {
  // no org filter: the same user id is shared across all of the user's org memberships
  const { data: activityByUser, isLoading } = useQueryGetUsersActivity(
    [userId],
    undefined,
    { enabled: !!userId }
  )

  const current = activityByUser?.[getUserActivityKey(orgId, userId)]
  // most recently active first, memberships without activity last
  const memberships = Object.values(activityByUser ?? {}).sort((a, b) =>
    a.last_seen_at && b.last_seen_at
      ? compareActivityTime(b.last_seen_at, a.last_seen_at)
      : compareActivityTime(a.last_seen_at, b.last_seen_at)
  )

  const stats = [
    ['Last login', current?.last_login_at, LAST_LOGIN_HINT],
    ['Last activity', current?.last_seen_at, LAST_ACTIVITY_HINT],
    ['First seen', current?.first_seen_at, FIRST_SEEN_HINT]
  ] as const

  return (
    <section
      aria-label="User activity"
      className="max-w-4xl space-y-6 rounded-xl bg-muted/20 p-6"
    >
      {/* identity: who this snapshot is about */}
      <header className="flex items-center gap-4">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/15 text-base font-semibold text-primary">
          {getInitials(name, email)}
        </div>
        <div className="min-w-0 space-y-0.5">
          <h2 className="truncate text-lg font-semibold">
            {name || email || 'Unknown user'}
          </h2>
          <p className="truncate text-sm text-muted-foreground">
            {email || '—'}
          </p>
          <p className="font-mono text-xs text-muted-foreground/80">
            User ID {userId ?? '—'} · Org {orgId ?? '—'}
          </p>
        </div>
      </header>

      {/* this organization */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {stats.map(([label, value, hint]) => (
          <div className="space-y-2 rounded-lg bg-muted/30 p-4" key={label}>
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {label}
            </div>
            {isLoading ? (
              <Pulse className="h-6 w-28" />
            ) : (
              <ActivityTime size="lg" value={value} />
            )}
            <p className="text-xs text-muted-foreground/70">{hint}</p>
          </div>
        ))}
      </div>

      {/* all organizations */}
      <div className="space-y-2">
        <h3 className="text-sm font-semibold">All organizations</h3>
        {isLoading ? (
          <Pulse className="h-16 w-full" />
        ) : memberships.length === 0 ? (
          <p className="rounded-lg bg-muted/30 p-4 text-sm text-muted-foreground">
            No activity recorded since tracking started.
          </p>
        ) : (
          <div className="overflow-hidden rounded-lg bg-muted/30">
            <Table>
              <TableHeader>
                <TableRow className="border-border/30 hover:bg-transparent">
                  <TableHead>Organization</TableHead>
                  <TableHead>Last login</TableHead>
                  <TableHead>Last activity</TableHead>
                  <TableHead>First seen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {memberships.map((row) => (
                  <TableRow
                    className="border-border/30 hover:bg-muted/20"
                    key={row.org_id}
                  >
                    <TableCell>
                      <Link
                        className="font-mono text-sm underline-offset-4 hover:underline"
                        to={`${RoutePaths.ORGANIZATIONS}/${row.org_id}`}
                      >
                        {row.org_id}
                      </Link>
                      {row.org_id === orgId && (
                        <Badge className="ml-2 border-transparent bg-primary/15 text-primary hover:bg-primary/15">
                          current
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <ActivityTime value={row.last_login_at} />
                    </TableCell>
                    <TableCell>
                      <ActivityTime value={row.last_seen_at} />
                    </TableCell>
                    <TableCell>
                      <ActivityTime value={row.first_seen_at} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <footer className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground/70">
        <span>{USER_ACTIVITY_HELP_TEXT}</span>
        <span className="tabular-nums">
          Snapshot {new Date().toLocaleString()}
        </span>
      </footer>
    </section>
  )
}
